import request from '@/utils/request'
import qs from 'qs'
import Cookies from 'js-cookie'

/**
 * 交易中心（订单列表 / 支付通道）接口
 *
 * 全部复用已有的支付模块接口：
 *   /payOrder/payorder/page               订单分页（返回 pageData + totalRechargeAmount）
 *   /payOrder/payorder/paymentPlatforms   支付通道 / 支付状态 / 支付类型的名称映射
 *   /payOrder/payorder/export             订单导出
 *   /payConfig/payconfig/page             支付通道配置分页
 *   /payConfig/payconfig/{id}             支付通道配置详情
 *
 * 后端没有「单笔限额」字段，该列为假数据（TODO 已标注）。
 */

/**
 * 订单分页
 * @param {Object} params - orderCode / userId / orderType / commodityType / startDate / endDate
 */
export function getPayOrderPage (params) {
  return request({
    url: '/payOrder/payorder/page',
    method: 'get',
    params
  })
}

/**
 * 支付平台配置（通道名称 / 支付状态 / 支付类型映射）
 */
export function getPaymentPlatforms () {
  return request({
    url: '/payOrder/payorder/paymentPlatforms',
    method: 'get'
  })
}

/**
 * 订单导出地址（与 view-module mixin 的导出方式保持一致，走浏览器下载）
 */
export function buildOrderExportUrl (params = {}) {
  const query = qs.stringify({
    token: Cookies.get('token'),
    ...params
  })
  return `${process.env.VUE_APP_API_TARGET}${process.env.VUE_APP_BASE_API}/payOrder/payorder/export?${query}`
}

/**
 * 支付通道配置分页
 */
export function getPayConfigPage (params) {
  return request({
    url: '/payConfig/payconfig/page',
    method: 'get',
    params
  })
}

/**
 * 支付通道导出地址（查询条件与分页一致，不分页）
 */
export function buildPayConfigExportUrl (params = {}) {
  const query = qs.stringify({
    token: Cookies.get('token'),
    ...params
  })
  return `${process.env.VUE_APP_API_TARGET}${process.env.VUE_APP_BASE_API}/payConfig/payconfig/export?${query}`
}

/**
 * 支付通道配置详情
 */
export function getPayConfigInfo (id) {
  return request({
    url: `/payConfig/payconfig/${id}`,
    method: 'get'
  })
}

/**
 * 更新支付通道配置（启用 / 禁用走这里）
 */
export function updatePayConfig (data) {
  return request({
    url: '/payConfig/payconfig/',
    method: 'put',
    data
  })
}

/**
 * 手动完成支付订单
 * 订单状态置为支付成功并发放对应权益，已支付成功的订单不能重复操作
 * @param {Object} data - { orderCode, remark, commission_state }
 *        commission_state: 1=向上级代理发放佣金 2=不发放（缺省按 2 处理）
 */
export function manualCompleteOrder (data) {
  return request({
    url: '/payOrder/payorder/manualComplete',
    method: 'post',
    data
  })
}

/**
 * 取消支付订单
 * 写入备注并把订单状态改为订单超时，已支付成功的订单不能取消
 * @param {Object} data - { orderCode, remark }
 */
export function cancelOrder (data) {
  return request({
    url: '/payOrder/payorder/cancel',
    method: 'post',
    data
  })
}

/**
 * 套餐（商品）分页
 */
export function getCommodityPage (params) {
  return request({
    url: '/commodity/commodity/page',
    method: 'get',
    params
  })
}

/**
 * 删除套餐（批量接口，单个删除也传数组）
 * @param {Array} ids - 商品 id 数组
 */
export function deleteCommodity (ids) {
  return request({
    url: '/commodity/commodity',
    method: 'delete',
    data: ids
  })
}

/**
 * 设置默认 / 推荐套餐
 * @param {String|Number} commodityId - 商品ID
 * @param {Number} defaultType - 1=我的页面 2=广告弹窗 3=兜底默认
 */
export function setCommodityDefault (commodityId, defaultType) {
  return request({
    url: '/commodity/commodity/setDefault',
    method: 'put',
    params: { commodityId, defaultType }
  })
}

// ---------- 字典 ----------

/**
 * 订单的商品类型（tb_pay_order.commodity_type）
 * 比商品表多一个「购买电影」，两者不要混用
 */
export const ORDER_COMMODITY_TYPE_MAP = {
  // 1: '钻石',
  2: 'VIP',
  3: '购买电影'
}

// 商品表的类型（tb_commodity.type）
export const COMMODITY_TYPE_MAP = {
  // 1: '钻石',
  2: 'VIP',
  3: '购买电影'
}

// 支付通道的支付类型
export const PAY_TYPE_MAP = {
  2: '微信',
  3: '支付宝'
}

/**
 * 订单的支付方式（tb_pay_order.pey_type）
 * 比支付通道多一个「数字货币」，两者不要混用
 */
export const ORDER_PAY_TYPE_MAP = {
  2: '微信',
  3: '支付宝',
  4: '数字货币'
}

// 商品类型（tb_commodity.type）
export const COMMODITY_TYPE = {
  VIP: 2,
  BUY_MOVIE: 3
}

// 商品规格（tb_commodity.commodity_specification）
export const COMMODITY_SPEC_OPTIONS = [
  { label: '包时（1小时）', value: 7 },
  { label: '包天（24小时）', value: 1 },
  { label: '包周（7天）', value: 2 },
  { label: '包月（30天）', value: 3 },
  { label: '包季（90天）', value: 4 },
  { label: '包年（365天）', value: 5 },
  { label: '终身', value: 6 }
]

export const COMMODITY_SPEC_MAP = COMMODITY_SPEC_OPTIONS.reduce((map, item) => {
  map[item.value] = item.label
  return map
}, {})

// 套餐是否开启
export const COMMODITY_STATUS = {
  ENABLED: 1,
  DISABLED: 2
}

// 套餐默认位
export const DEFAULT_TYPE_OPTIONS = [
  { label: '我的页面', value: 1, field: 'isDefaultEntry1' },
  { label: '广告弹窗', value: 2, field: 'isDefaultEntry2' },
  { label: '兜底默认', value: 3, field: 'isDefault' }
]

// 支付通道状态
export const CONFIG_STATUS = {
  ENABLED: 1,
  DISABLED: 2
}

export const CONFIG_STATUS_MAP = {
  1: '启用',
  2: '关闭'
}

// 支付通道的支付类型
export const PAY_CONFIG_TYPE_OPTIONS = [
  { label: '微信', value: 2 },
  { label: '支付宝', value: 3 }
]
